import { useTranslation } from 'react-i18next'
import { ArrowRight, CircleCheckBig } from 'lucide-react'
import { cn } from '@playbooks/ui/lib/utils'
import { ActionCardsElement } from '@playbooks/action-cards'
import { Button } from '@playbooks/ui/components/button'
import { ViewMode } from '@playbooks/action-cards-utils'

import { SliderManager, Step, StepType, WebPlayLocalizationService } from '../types'
import { isColorDark } from '../utils/isColorDark'
import { toast } from 'sonner'
import ResetButton from './ui/ResetButton'
import LanguageSwitcher from './LanguageSwitcher'

interface SliderNavigationProps {
  viewMode: ViewMode
  accentColor: string
  actionCardsElementRef: React.RefObject<ActionCardsElement | null>
  openSheet: () => void
  sliderManagerRef: React.RefObject<SliderManager | null>
  isFinishButtonVisible: boolean
  step: Step | null
  stepsCount: number
  localizationService?: WebPlayLocalizationService | null
}

const SliderNavigation = ({
  viewMode,
  accentColor,
  actionCardsElementRef,
  openSheet,
  sliderManagerRef,
  isFinishButtonVisible,
  step,
  stepsCount,
  localizationService,
}: SliderNavigationProps) => {
  const { t } = useTranslation()
  const isAccentColorDark = isColorDark(accentColor)

  const isFirst = step?.type === StepType.FIRST || step === null
  const isLast = step?.type === StepType.LAST
  const isFunnelView = viewMode === ViewMode.FUNNEL

  const handleNextBtnClick = () => {
    const currentStep = sliderManagerRef.current?.getCurrentStep?.()
    const slidesData = actionCardsElementRef.current?.getSlidesData?.()

    if (!slidesData?.slides) {
      return
    }

    const { slides, canProceed } = slidesData

    if (!canProceed) {
      toast.warning(
        t(
          'submit.incompleteEmailFields',
          'You have incomplete email fields. Check the data you entered and try again.',
        ),
      )
    }

    if (slides && currentStep) {
      const slide = slides[currentStep.step]

      actionCardsElementRef.current?.highlightElementIds?.(slide?.requiredDataIds || [])

      if (!slides[currentStep.step]?.isFilled || !canProceed) return
    }

    sliderManagerRef.current?.nextSlide?.()
  }

  const handlePrevBtnClick = () => {
    const slidesData = actionCardsElementRef.current?.getSlidesData?.()

    if (!slidesData) {
      return
    }

    if (!slidesData.canProceed) {
      actionCardsElementRef.current?.highlightElementIds?.([])

      toast.warning(
        t(
          'submit.incompleteEmailFields',
          'You have incomplete email fields. Check the data you entered and try again.',
        ),
      )

      return
    }

    sliderManagerRef.current?.prevSlide?.()
  }

  // Reset is visible on the first funnel step, and always visible when not in funnel view
  const isResetButtonVisible = (isFunnelView && isFirst) || !isFunnelView

  // Back is visible on any non-first funnel step
  const isBackButtonVisible = isFunnelView && !isFirst

  // Next is visible on any non-last funnel step and when there is more than one step
  const isNextButtonVisible = isFunnelView && !isLast && stepsCount > 1

  return (
    <div className={cn('relative grid h-9 w-full grid-cols-3 gap-x-1')}>
      <div className="col-span-1">
        {isResetButtonVisible && <ResetButton />}
        <SliderButton
          isLeft
          hidden={!isBackButtonVisible}
          onClick={handlePrevBtnClick}
          accentColor={accentColor}
          isAccentColorDark={isAccentColorDark}
          viewMode={viewMode}
        >
          {t('submit.backBtn', 'Back')}
        </SliderButton>
      </div>

      <div className="flex col-span-1 items-center justify-center gap-2">
        {localizationService && isFunnelView && !isFinishButtonVisible && (
          <LanguageSwitcher service={localizationService} />
        )}
        <Button
          data-testid="complete-button"
          type="button"
          onClick={openSheet}
          style={{ background: accentColor, borderColor: isAccentColorDark ? undefined : accentColor }}
          className={cn(
            !isFinishButtonVisible ? 'hidden' : 'flex',
            isAccentColorDark && 'border-zinc-800! border',
            'gap-2 items-center justify-center px-4 w-full max-w-max text-white',
          )}
        >
          <CircleCheckBig size={16} />

          <span>{t('submit.browLabel', 'Complete')}</span>
        </Button>
      </div>

      <div className="flex col-span-1 justify-end">
        {localizationService && !isFunnelView && <LanguageSwitcher service={localizationService} />}
        <SliderButton
          hidden={!isNextButtonVisible}
          onClick={handleNextBtnClick}
          accentColor={accentColor}
          isAccentColorDark={isAccentColorDark}
          viewMode={viewMode}
        >
          {t('submit.nextBtn', 'Next')}
        </SliderButton>
      </div>
    </div>
  )
}

interface SliderButtonProps {
  hidden: boolean
  isLeft?: boolean
  children: React.ReactNode
  onClick: () => void
  viewMode: ViewMode
  accentColor: string
  isAccentColorDark: boolean
}

const SliderButton = ({
  isLeft,
  hidden,
  children,
  viewMode,
  accentColor,
  isAccentColorDark,
  onClick,
}: SliderButtonProps) => {
  if (viewMode !== 'funnel') {
    return null
  }

  return hidden ? null : (
    <Button
      onClick={onClick}
      style={{ background: accentColor, borderColor: isAccentColorDark ? undefined : accentColor }}
      className={cn(
        'flex items-center justify-center text-white hover:bg-wp-dark px-4',
        'dark:border dark:border-white gap-2',

        isAccentColorDark && 'border-zinc-800! border border-solid',
      )}
    >
      {isLeft && (
        <div style={{ transform: 'rotate(180deg)' }}>
          <ArrowRight />
        </div>
      )}

      {children}

      {!isLeft && (
        <div>
          <ArrowRight />
        </div>
      )}
    </Button>
  )
}

export default SliderNavigation
